import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { MessageCircle, StickyNote, Save, Trash2, History, ChevronDown } from "lucide-react";

function normalizeWa(num) {
  let n = String(num || "").replace(/[^0-9]/g, "");
  if (!n) return "";
  if (n.startsWith("620")) n = "62" + n.slice(3);
  else if (n.startsWith("0")) n = "62" + n.slice(1);
  else if (!n.startsWith("62") && n.startsWith("8")) n = "62" + n;
  return n;
}

function timeAgo(iso, t) {
  if (!iso) return null;
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return t("crm.justNow");
  if (m < 60) return t("crm.minAgo").replace("{n}", m);
  const h = Math.floor(m / 60);
  if (h < 24) return t("crm.hourAgo").replace("{n}", h);
  return t("crm.dayAgo").replace("{n}", Math.floor(h / 24));
}

const fmtWhen = (iso) => new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" });

export function WhatsAppReminder({ memberName, whatsappNumber, context, memberUserId, lastRemindedAt, coachNote, templates = [], onTemplatesChanged, onLogged, onNoteSaved, testId }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState(coachNote || "");
  const [busy, setBusy] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplTitle, setTplTitle] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);

  const hasWa = !!normalizeWa(whatsappNumber);
  const name = memberName || t("crm.defaultName");
  const template = (context === "EDUCATION" ? t("crm.tplEducation") : t("crm.tplFoodJournal")).replace("{name}", name);
  const applicable = templates.filter((tp) => tp.context === "ANY" || tp.context === context);

  const openDialog = () => {
    setMsg(template); setNote(coachNote || "");
    setShowHistory(false); setHistory(null); setSavingTpl(false); setTplTitle("");
    setOpen(true);
  };

  const applyTpl = (tp) => setMsg(tp.body.replace(/\{name\}/g, name));

  const sendWa = async () => {
    const n = normalizeWa(whatsappNumber);
    if (!n) return;
    window.open(`https://wa.me/${n}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    try {
      const { data } = await api.post("/crm/reminders", { member_user_id: memberUserId, context });
      onLogged?.(data.data.last_reminded_at);
      setHistory(null); // force refetch next time history opens
    } catch (e) { toast.error(extractError(e).message); }
    setOpen(false);
  };

  const saveNote = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/crm/notes/${memberUserId}`, { note });
      onNoteSaved?.(data.data.coach_note);
      toast.success(t("crm.noteSaved"));
    } catch (e) { toast.error(extractError(e).message); } finally { setBusy(false); }
  };

  const saveAsTemplate = async () => {
    const title = tplTitle.trim();
    if (!title || !msg.trim()) return;
    setBusy(true);
    try {
      const body = name ? msg.split(name).join("{name}") : msg; // store reusable with {name}
      await api.post("/crm/templates", { title, body, context });
      toast.success(t("crm.templateSaved"));
      setSavingTpl(false); setTplTitle("");
      onTemplatesChanged?.();
    } catch (e) { toast.error(extractError(e).message); } finally { setBusy(false); }
  };

  const deleteTpl = async (id) => {
    try { await api.delete(`/crm/templates/${id}`); toast.success(t("crm.templateDeleted")); onTemplatesChanged?.(); }
    catch (e) { toast.error(extractError(e).message); }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) {
      try { const { data } = await api.get(`/crm/reminders/${memberUserId}`); setHistory(data.data); }
      catch { setHistory([]); }
    }
  };

  const rel = timeAgo(lastRemindedAt, t);

  return (
    <div className="flex flex-col items-start gap-1">
      <Button type="button" size="sm" data-testid={testId} disabled={!hasWa} onClick={openDialog}
        className="h-8 bg-[#25D366] hover:bg-[#1EBE5B] text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
        <MessageCircle className="h-4 w-4 mr-1" /> {t("crm.chatWhatsapp")}
      </Button>
      {!hasWa && <span data-testid={`${testId}-no-wa`} className="text-[10px] text-slate-500">{t("crm.noWhatsapp")}</span>}
      {rel && <span data-testid={`${testId}-last`} className="text-[10px] text-slate-500">{t("crm.lastReminded")}: {rel}</span>}
      {coachNote && (
        <button type="button" onClick={openDialog} data-testid={`${testId}-note`}
          className="flex items-center gap-1 text-left text-[10px] text-amber-300/80 hover:text-amber-200 max-w-[220px] truncate">
          <StickyNote className="h-3 w-3 shrink-0" /> <span className="truncate">{coachNote}</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#161923] border-white/10 text-slate-200 max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{t("crm.dialogTitle")} · {name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Saved templates */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400">{t("crm.templatesLabel")}</label>
              {applicable.length === 0 ? (
                <p className="text-[11px] text-slate-600">{t("crm.noTemplates")}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-templates`}>
                  {applicable.map((tp) => (
                    <span key={tp.template_id} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 pl-2.5 pr-1 py-0.5 text-[11px]">
                      <button type="button" data-testid={`${testId}-tpl-${tp.template_id}`} onClick={() => applyTpl(tp)} className="text-slate-200 hover:text-white">{tp.title}</button>
                      <button type="button" data-testid={`${testId}-tpl-del-${tp.template_id}`} onClick={() => deleteTpl(tp.template_id)} className="text-slate-500 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Editable message */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400">{t("crm.messageLabel")}</label>
              <Textarea data-testid={`${testId}-message`} value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
                className="bg-[#1C202E] border-white/10 text-white resize-none" />
              {savingTpl ? (
                <div className="flex gap-2 items-center pt-1">
                  <input data-testid={`${testId}-tpl-title`} value={tplTitle} onChange={(e) => setTplTitle(e.target.value)}
                    placeholder={t("crm.templateTitlePlaceholder")} maxLength={80}
                    className="flex-1 h-9 rounded-md bg-[#1C202E] border border-white/10 px-3 text-sm text-white" />
                  <Button type="button" size="sm" disabled={busy || !tplTitle.trim()} onClick={saveAsTemplate} data-testid={`${testId}-tpl-save`}
                    className="h-9 bg-white/10 hover:bg-white/15 text-white">{t("crm.save")}</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setSavingTpl(false)} className="h-9 text-slate-400 hover:bg-white/5">{t("common.cancel")}</Button>
                </div>
              ) : (
                <button type="button" data-testid={`${testId}-tpl-add`} onClick={() => setSavingTpl(true)}
                  className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-white pt-0.5"><Save className="h-3 w-3" /> {t("crm.saveAsTemplate")}</button>
              )}
              <p className="text-[10px] text-slate-600">{t("crm.nameHint")}</p>
            </div>

            {/* Per-member note */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400">{t("crm.noteLabel")}</label>
              <div className="flex gap-2">
                <Textarea data-testid={`${testId}-note-input`} value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder={t("crm.notePlaceholder")} className="bg-[#1C202E] border-white/10 text-white flex-1 resize-none" />
                <Button type="button" variant="ghost" disabled={busy} onClick={saveNote} data-testid={`${testId}-save-note`}
                  className="self-start text-slate-300 hover:bg-white/5">{t("crm.saveNote")}</Button>
              </div>
            </div>

            {/* Reminder history */}
            <div className="space-y-1.5">
              <button type="button" data-testid={`${testId}-history-toggle`} onClick={toggleHistory}
                className="flex items-center gap-1 text-xs text-slate-300 hover:text-white">
                <History className="h-3.5 w-3.5" /> {t("crm.history")}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showHistory ? "rotate-180" : ""}`} />
              </button>
              {showHistory && (
                <div data-testid={`${testId}-history`} className="rounded-lg border border-white/10 bg-[#10121B] p-2 max-h-40 overflow-y-auto">
                  {history === null ? (
                    <p className="text-[11px] text-slate-500">{t("common.loading")}</p>
                  ) : history.length === 0 ? (
                    <p className="text-[11px] text-slate-500">{t("crm.historyEmpty")}</p>
                  ) : (
                    <ul className="space-y-1">
                      {history.map((h) => (
                        <li key={h.reminder_id} className="flex items-center justify-between text-[11px] text-slate-400">
                          <span>{fmtWhen(h.created_at)} WIB</span>
                          <span className="text-slate-600 uppercase tracking-wider">{t(h.context === "EDUCATION" ? "crm.ctxEducation" : "crm.ctxFood")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-300 hover:bg-white/5">{t("common.cancel")}</Button>
            <Button type="button" onClick={sendWa} disabled={!hasWa} data-testid={`${testId}-open-wa`}
              className="bg-[#25D366] hover:bg-[#1EBE5B] text-black font-semibold">
              <MessageCircle className="h-4 w-4 mr-1" /> {t("crm.openWhatsapp")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
